// The signed-in session, in one place. App.tsx starts it: /api/me is asked
// once, and while someone is signed in this device follows their list live
// (startSync); signed out, or the session gone, it stops. A visitor never
// opens a socket and never calls anything that spends.
//
// The sign-up wall lives here too. Anything that needs an account (Generate,
// a snag) awaits requireAccount(): a visitor sees the sign-in sheet, and once
// they are in, everything they did on this device moves into their list
// before anything else happens. That is every project the device holds (the
// demo projects under their fixed ids, tombstones too) as a create, then the
// outbox, through POST /api/lists/:listId/changes. The list merges a create
// into a copy it already has by editedAt, item by item, so a second device
// signing in to the same account adds what is new, keeps both sets of ticks,
// and ends with one copy of each demo project.
//
// The move happens once per device and list: the device store records the
// list its move landed in (store.listId(), kept beside lastSeq and wiped
// with it), and a device holding that list's copies only syncs, since
// resending them could only lose what it has not heard yet. Sync starts only
// after the move, for the same reason.
//
// A device holding another list's copy is cleared before anything moves or
// syncs (clearDevice, app/auth/me.ts): a suspended person's device keeps
// their list (4003 below), and so does one that launched offline and reads
// as a visitor's, and whoever signs in on it next must not carry that list,
// its outbox or its lastSeq into their own.
//
// Sync stops when the list closes the socket on purpose: 4001, the session
// ended, clears the device (sessionEnded); 4003, the account is suspended,
// keeps everything and only stops (accountPaused).
import { useEffect } from 'react';
import type { ChangesResponse, Me } from '@shared/api';
import type { Change } from '@shared/edit';
import type { Project } from '@shared/plan';
import { ApiFailure, api } from './api';
import { accountPaused, clearDevice, meStore, refetchMe, sessionEnded, useMe, type MeState } from './auth/me';
import { setRemoteMediaList } from './store/media';
import { store as appStore, type Store } from './store/store';
import { backoff, startSync as appStartSync } from './sync/sync';

/** The changes route's limits (worker/list/validate.ts), with room for the envelope. */
export const MOVE_LIMITS = { changes: 200, bytes: 900 * 1024 } as const;

export type PostChanges = (listId: string, changes: Change[]) => Promise<ChangesResponse>;

export const postChanges: PostChanges = (listId, changes) =>
  api<ChangesResponse>(`/api/lists/${encodeURIComponent(listId)}/changes`, { body: { changes } });

/**
 * A project as it moves into a list. One never edited (a demo project as
 * seeded) goes with editedAt 0, the oldest real stamp: left out, the list
 * would stamp it with the time of the move, and a copy nobody touched would
 * then outrank a rename made on another device since.
 */
export function outgoing(p: Project): Project {
  return p.editedAt === undefined ? { ...p, editedAt: 0 } : p;
}

/** In order, at most MOVE_LIMITS.changes to a request and under MOVE_LIMITS.bytes of JSON. */
export function batches(changes: readonly Change[], limits: { changes: number; bytes: number } = MOVE_LIMITS): Change[][] {
  const out: Change[][] = [];
  let batch: Change[] = [];
  let bytes = 0;
  for (const change of changes) {
    const size = new TextEncoder().encode(JSON.stringify(change)).byteLength + 1;
    if (batch.length && (batch.length >= limits.changes || bytes + size > limits.bytes)) {
      out.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(change);
    bytes += size;
  }
  if (batch.length) out.push(batch);
  return out;
}

export interface MoveDeps {
  store?: Store;
  post?: PostChanges;
  now?: () => number;
}

/**
 * Moves everything on this device into the list: a create for every
 * project it holds, then every change still in the outbox, which then
 * leaves it. Throws the first failure (an ApiFailure); what landed before it
 * stays landed, and a second try is safe because a create merges.
 */
export async function moveIntoList(listId: string, deps: MoveDeps = {}): Promise<void> {
  const store = deps.store ?? appStore;
  const post = deps.post ?? postChanges;
  await store.ready;
  // One look, with nothing awaited in between: the documents and the outbox
  // that made them, so acknowledging these changes loses nothing.
  const pending = store.pending();
  const at = (deps.now ?? Date.now)();
  const creates: Change[] = store.projects().map((p) => ({
    id: crypto.randomUUID(), projectId: p.id, at, deviceId: store.deviceId, edit: { kind: 'create', project: outgoing(p) },
  }));
  const mine = new Set(pending.map((c) => c.id));
  // Creates first: a tick that reached the list before its project would be dropped there.
  for (const batch of batches([...creates, ...pending])) {
    const { applied } = await post(listId, batch);
    for (const id of applied) if (mine.has(id)) await store.acknowledge(id);
  }
}

// --- The session

export interface SessionDeps {
  store?: Store;
  startSync?: (listId: string) => () => void;
  post?: PostChanges;
  refetch?: () => Promise<void>;
  me?: { get(): MeState; subscribe(listener: () => void): () => void };
  /** Clears a device that holds another list's copy, leaving who is signed in alone; afterwards `store` is a visitor's. */
  clear?: () => Promise<void>;
  setTimeout?: (f: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

type Phase = 'moving' | 'live' | 'failed';
interface Live { listId: string; phase: Phase; attempt: Promise<void>; failures: number; stop?: () => void; retry?: unknown }

/** A move the list refused outright (too large, not a shape it takes) is not retried: sync goes on without it. */
const refusal = (err: unknown): boolean => err instanceof ApiFailure && (err.status === 400 || err.status === 413);
/** The session is gone, or the list is not this person's: ask who is signed in again. */
const lapsed = (err: unknown): boolean => err instanceof ApiFailure && [401, 403, 404].includes(err.status);

export function createSession(deps: SessionDeps = {}) {
  const store = deps.store ?? appStore;
  const startSync = deps.startSync ?? ((listId: string) => appStartSync(listId, { onEnded: () => void sessionEnded(), onSuspended: accountPaused }));
  const refetch = deps.refetch ?? refetchMe;
  const me = deps.me ?? meStore;
  const clear = deps.clear ?? clearDevice;
  const later = deps.setTimeout ?? ((f: () => void, ms: number) => setTimeout(f, ms));
  const cancel = deps.clearTimeout ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));

  let live: Live | null = null;

  function begin(s: Live): Promise<void> {
    cancel(s.retry);
    s.retry = undefined;
    s.phase = 'moving';
    s.attempt = (async () => {
      await store.ready;
      if (store.listId() === s.listId) return;
      // Another list's copy (lastSeq for a device that synced before the store named its list).
      if (store.listId() !== null || store.lastSeq() > 0) await clear();
      if (live !== s) return;
      try {
        await moveIntoList(s.listId, { store, post: deps.post });
      } catch (err) {
        if (!refusal(err)) throw err;
      }
      await store.setListId(s.listId);
    })().then(
      () => {
        if (live !== s) return;
        s.phase = 'live';
        s.failures = 0;
        setRemoteMediaList(s.listId);
        s.stop = startSync(s.listId);
      },
      (err: unknown) => {
        if (live === s) {
          s.phase = 'failed';
          if (lapsed(err)) void refetch().catch(() => undefined);
          else s.retry = later(() => { if (live === s && s.phase === 'failed') void begin(s).catch(() => undefined); }, backoff(s.failures++));
        }
        throw err;
      },
    );
    return s.attempt;
  }

  /**
   * This device, in this person's list and following it live: the move
   * first if it has not happened, then sync. One per list, whoever asks
   * (the session following /api/me, or the wall); a failed one tries again.
   */
  function ensure(who: Me): Promise<void> {
    if (live?.listId === who.listId) return live.phase === 'failed' ? begin(live) : live.attempt;
    end();
    live = { listId: who.listId, phase: 'moving', attempt: Promise.resolve(), failures: 0 };
    return begin(live);
  }

  /** Signed out: no socket, nothing waiting to retry. */
  function end(): void {
    setRemoteMediaList(null);
    if (!live) return;
    cancel(live.retry);
    live.stop?.();
    live = null;
  }

  // --- The wall

  let wallOpen = false;
  let answer: ((who: Me | null) => void) | null = null;
  let asking: Promise<Me | null> | null = null;
  const wallListeners = new Set<() => void>();
  const setWall = (open: boolean) => {
    wallOpen = open;
    for (const l of wallListeners) l();
  };
  const settle = (who: Me | null) => {
    setWall(false);
    const done = answer;
    answer = null;
    done?.(who);
  };

  /** For the sheet App.tsx shows: whether it is open, and what came of it. */
  const wall = {
    get: (): boolean => wallOpen,
    subscribe(listener: () => void): () => void {
      wallListeners.add(listener);
      return () => { wallListeners.delete(listener); };
    },
    signedIn: (who: Me) => settle(who),
    closed: () => settle(null),
  };

  /** Who is signed in, once /api/me has answered. */
  function settled(): Promise<Me | null> {
    const now = me.get();
    if (now !== 'loading') return Promise.resolve(now);
    return new Promise((resolve) => {
      const off = me.subscribe(() => {
        const next = me.get();
        if (next === 'loading') return;
        off();
        resolve(next);
      });
    });
  }

  /**
   * The person, signed in and moved in, or null if they would rather not.
   * A visitor sees the sign-in sheet first; callers waiting at once share it.
   * Rejects (an ApiFailure) when the move into their list could not be made
   * right now; it is tried again on its own, and by the next call.
   */
  async function requireAccount(): Promise<Me | null> {
    let who = await settled();
    if (!who) {
      asking ??= new Promise<Me | null>((resolve) => {
        answer = resolve;
        setWall(true);
      }).finally(() => { asking = null; });
      who = await asking;
      if (!who) return null;
    }
    await ensure(who);
    return who;
  }

  /** Follows /api/me from now on; returns the stop. */
  function start(): () => void {
    const follow = () => {
      const who = me.get();
      if (who === 'loading') return;
      if (who === null) end();
      else void ensure(who).catch(() => undefined);
    };
    const off = me.subscribe(follow);
    follow();
    // Back online: a visitor may have been a member all along (an offline
    // launch reads as signed out), and a move that failed tries again now.
    const online = () => {
      const who = me.get();
      if (who === null) void refetch().catch(() => undefined);
      else if (who !== 'loading' && live?.phase === 'failed') void begin(live).catch(() => undefined);
    };
    if (typeof addEventListener === 'function') addEventListener('online', online);
    return () => {
      off();
      if (typeof removeEventListener === 'function') removeEventListener('online', online);
      end();
    };
  }

  return {
    start,
    ensure,
    end,
    requireAccount,
    wall,
    /** Test only: what the session is doing. */
    phase: (): Phase | null => live?.phase ?? null,
  };
}

// The app's one session.
const session = createSession();

/** The wall's sheet, for App.tsx. */
export const wall = session.wall;

/**
 * Resolves to the signed-in person once everything this device did is in
 * their list, showing the sign-in sheet to a visitor first; null if they
 * close it. Rejects with an ApiFailure when the move cannot be made now.
 */
export const requireAccount = (): Promise<Me | null> => session.requireAccount();

/** App.tsx calls this once: asks /api/me and follows it with sync. */
export function useSession(): void {
  useMe();
  useEffect(() => session.start(), []);
}
